/** O título da aba do navegador: HTG | o módulo aberto (Samuel, 05/10/2026: "aparecer
 *  em qual modulo está aberto, por exemplo HubTransGrãos | Viagens"; a sigla HTG no
 *  lugar do nome inteiro foi pedido do Amorim).
 *
 *  O módulo é o item do MENU cujo endereço contém a rota atual, e vence o mais longo:
 *  `/logistica/viagens/lista` é Viagens, `/logistica/conjuntos/transferencias` é
 *  Transferências e não Conjuntos, e o detalhe de uma descarga continua sendo Painel
 *  de Descarga. O rótulo é o mesmo da barra lateral, no idioma escolhido.
 *
 *  As telas fora do menu, como a entrada e a conta, têm rótulo próprio em
 *  `FORA_DO_MENU`. Rota que não casa com nada fica só com a sigla.
 *
 *  As telas de entrada vestem o hub escolhido no seletor do login, e a aba leva a
 *  sigla dele: HVF quando é o HubVerticalFarm, desde 28/10/2026. Dentro do sistema,
 *  desde 02/11/2026, a sigla é a do hub da rota: HVF debaixo de `/farm`, HTG nas
 *  áreas do HubTransGrãos e no portal, e a do hub guardado na rota neutra. */
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useI18n } from '../i18n/useI18n'
import type { MessageKey } from '../i18n/messages'
import { flattenNav, portalNav } from './navConfig'
import { SEM_ACESSO_PATH } from './paths'
import { AREAS, AREAS_PATH, hubDoCaminho } from '../areas/areas'
import { HUB_PADRAO, hubDef, useHubLembrado, type HubKey } from '../hubs/hubs'

export const SIGLA = 'HTG'

interface Destino {
  path: string
  label: string
  labelKey?: MessageKey
}

const FORA_DO_MENU: Destino[] = [
  { path: '/login', label: 'Entrar', labelKey: 'auth.loginPage.signIn' },
  { path: '/esqueci-senha', label: 'Esqueci a senha', labelKey: 'tabTitle.forgotPassword' },
  { path: '/primeiro-acesso', label: 'Primeiro acesso', labelKey: 'tabTitle.firstAccess' },
  { path: SEM_ACESSO_PATH, label: 'Sem acesso', labelKey: 'tabTitle.noAccess' },
  { path: AREAS_PATH, label: 'Áreas', labelKey: 'tabTitle.areas' },
  { path: '/conta/seguranca', label: 'Segurança', labelKey: 'tabTitle.security' },
  { path: '/conta/configuracoes', label: 'Configurações', labelKey: 'account.settings' },
  { path: '/logistica/contracts', label: 'Contratos', labelKey: 'nav.contracts' },
  { path: '/portal/solicitar', label: 'Solicitações', labelKey: 'nav.requests' },
]

// O início do DP tem o rótulo genérico Início no menu da área; na aba, vale o nome
// da área, como o de qualquer outro módulo.
const DESTINOS: Destino[] = [
  ...AREAS.flatMap((area) => flattenNav(area.nav)).filter((item) => item.path !== '/dp'),
  { path: '/dp', label: 'Departamento Pessoal', labelKey: 'areas.dp.nome' },
  ...flattenNav(portalNav),
  ...FORA_DO_MENU,
]

function contem(path: string, pathname: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`)
}

export function tituloDaAba(pathname: string, t: (key: MessageKey) => string, hub: HubKey = HUB_PADRAO): string {
  const sigla = hubDef(hubDoCaminho(pathname) ?? hub).sigla
  const destino = DESTINOS
    .filter((d) => contem(d.path, pathname))
    .reduce<Destino | null>((maior, d) => (!maior || d.path.length > maior.path.length ? d : maior), null)
  if (!destino) return sigla
  const rotulo = destino.labelKey ? t(destino.labelKey) : destino.label
  return `${sigla} | ${rotulo === destino.labelKey ? destino.label : rotulo}`
}

/** Mantém `document.title` em dia com a rota, com o idioma e com o hub da entrada. */
export function useTituloDaAba(): void {
  const { pathname } = useLocation()
  const { t } = useI18n()
  const hub = useHubLembrado()
  useEffect(() => {
    document.title = tituloDaAba(pathname, t, hub)
  }, [pathname, t, hub])
}
